import { blobToBase64String } from 'blob-util';
import { produce } from 'immer';
import { z } from 'zod';

import { ApiFetchStatus, AppDetailsFetchErrorType } from '@tdp/common/constants';
import { ILogger } from '@tdp/common/telemetry';
import { parseApiResponse } from '@tdp/core/util/parseApiResponse';
import {
  IAppDetailsReader,
  AppDetailsFetchResult,
  IAppDetailsSchema,
  IAppDetails,
  IAppDetailsWriter,
  AppDetailsWriteResult,
  IAppLobPublishStatusFetchResult,
  IAppLobDeleteRequestResult,
  AppLobPublish,
  IAppDetailsLanguageFile
} from '@tdp/modules/apps';
import {
  IAppIconWriter,
  IAppIconWriteResult,
  ImageDefinitionsDtoSchema
} from '@tdp/modules/apps';

import { IAppDetailsDto, IAppDetailsDtoSchema } from './appDetailsDTO';
import { IAppLangDeleteRequestResult, IAppLangPublishStatusFetchResult } from '../abstractions/AppLanguageAbstractions';
import { IAppOwnersFetchResult } from '../abstractions/AppOwnersAbstractions';
import { IAppOwner } from '../abstractions/IAppOwners';

export interface TdpAppDetailsServiceDeps {
  logger: ILogger;
  apiFetch: typeof fetch;
}

export class TdpAppDetailsService implements IAppDetailsReader, IAppDetailsWriter, IAppIconWriter {
  private logger: ILogger;
  private apiFetch: typeof fetch;

  constructor({ logger, apiFetch }: TdpAppDetailsServiceDeps) {
    this.apiFetch = apiFetch;
    this.logger = logger;
  }

  async writeAppDetails(appDetails: IAppDetails): Promise<AppDetailsWriteResult> {
    // This is a hack, but making it an internal call just to make it explicit.
    return await this.writeAppDetailsPreserveServiceProps(appDetails);
  }

  private async mutateServiceDefinition(appId: string, mutator: ((serviceDefinition: IAppDetailsDto) => IAppDetailsDto)): Promise<AppDetailsWriteResult> {
    // Read the current value from the service
    const currentServiceDefinitionResult = await this.apiFetch(`/appdefinitions/v2/${appId}`);
    if (currentServiceDefinitionResult.status != 200) {
      this.logger.error(
        'Network error when fetching app definition for app id: %s | reason: %s',
        appId,
        currentServiceDefinitionResult.statusText
      );
      return {
        type: 'error',
        errorType: 'networkError',
        errorMessage: currentServiceDefinitionResult.statusText,
      };
    }
    const defReadDTO = (await currentServiceDefinitionResult.json()) as unknown;
    const defRead = IAppDetailsDtoSchema.safeParse(defReadDTO);
    if (!defRead.success) {
      this.logger.error(
        'Encountered parsing errors with payload from service for app id: %s',
        appId
      );
      return { type: 'error', errorType: 'networkError', errorMessage: defRead.error.message };
    }

    const writeObject = mutator(defRead.data);
    // Write the value back... because reasons.
    await this.apiFetch(`/appdefinitions/v2/${appId}`, {
      method: 'POST',
      body: JSON.stringify(writeObject),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const appDetails = IAppDetailsSchema.parse(writeObject);

    return { type: 'success', appDetails };
  }

  // This is a hack to preseve the API's properties when writing the app back to the service. 
  // The API should support some version of patching given the size & scope of the app details and
  // given that it is trasmitting internal details like azure blob file locations.
  private async writeAppDetailsPreserveServiceProps(appDetails: IAppDetails): Promise<AppDetailsWriteResult> {
    return this.mutateServiceDefinition(appDetails.appId, (def) => {
      return Object.assign(def, appDetails);
    });
  }

  async deleteLobAppPublishRequest(id: string, internalId: string): Promise<IAppLobDeleteRequestResult> {
    const response = await this.apiFetch(`/publishing/${internalId}/appdefinitions/${id}`, {
      method: 'DELETE',
    });
    if (response.status === 204) {
      return {
        type: ApiFetchStatus.SUCCESS,
      };
    }
    return {
      type: ApiFetchStatus.ERROR,
      errorType: AppDetailsFetchErrorType.NETWORK_ERROR,
      errorMessage: response.statusText,
    }
  }

  async createLobPublishRequest(blob: Blob): Promise<IAppLobPublishStatusFetchResult> {
    const response = await this.apiFetch(`/publishing`, {
      method: 'POST',
      headers: {
        "Content-Type": "application/zip; charset=utf-8"
      },
      body: blob,
    });
    if (response.status !== 200) {
      return {
        type: ApiFetchStatus.ERROR,
      }
    }
    const responseObject = (await response.json());
    if (responseObject?.error) {
      return {
        type: ApiFetchStatus.ERROR
      }
    }
    return {
      type: ApiFetchStatus.SUCCESS,
    }
  }

  async readLobAppPublishStatus(id: string): Promise<IAppLobPublishStatusFetchResult> {
    const response = await this.apiFetch(`/publishing/${id}`);
    const parsedResponse = await parseApiResponse({
      response: response,
      errorMessage: `Error getting app lob publish status for ${id}`,
      logger: this.logger,
      zodType: AppLobPublish,
    });
    return {
      type: parsedResponse.type,
      ...(parsedResponse.errorType && { errorType: parsedResponse.errorType }),
      ...(parsedResponse.data && { response: parsedResponse.data }),
      ...(parsedResponse.errorMessage && { errorMessage: parsedResponse.errorMessage }),
    }
  };

  async readAppDetails(id: string): Promise<AppDetailsFetchResult> {
    try {
      const result = await this.apiFetch(`/appdefinitions/${id}`);
      if (result.status !== 200) {
        this.logger.error(
          'Network error when fetching app details for app id: %s | reason: %s',
          id,
          result.statusText
        );
        return {
          type: 'error',
          errorType: 'networkError',
          errorMessage: result.statusText,
        };
      }

      const dto = (await result.json()) as unknown;
      const maybeSuccess = IAppDetailsSchema.safeParse(dto);
    //TODO(nibeauli): this is going to repeat, we should standardize the error handling for parsing errors from service.
      if (!maybeSuccess.success) {
        this.logger.error(
          'Encountered parsing errors with payload from service for app id: %s',
          id
        );
        for (const error of maybeSuccess.error.errors) {
          this.logger.error('\t[%s]: %s', error.path.join('.'), error.message);
          console.log('\t[%s]:!! %s', error.path.join('.'), error.message);
        }
        return {
          type: 'error',
          errorType: 'parsingError',
          errorMessage: maybeSuccess.error.errors
            .map((e) => e.message)
            .join(', '),
        };
      }

      return {
        type: 'success',
        appDetails: maybeSuccess.data,
      };
    } catch (error) {
      this.logger.error(
        'Encountered errors on network response payload'
      );
      return {
        type: 'error',
        errorType: 'error',
        errorMessage:
          error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }
  async deleteLanguage(id: string, appId: string): Promise<IAppLangDeleteRequestResult> {
    const response = await this.apiFetch(`/appdefinitions/${appId}/languages/${id}`, {
      method: 'DELETE',
    });
    if (response.status === 200) {
      return {
        type: ApiFetchStatus.SUCCESS,
      };
    }
    return {
      type: ApiFetchStatus.ERROR,
      errorType: AppDetailsFetchErrorType.NETWORK_ERROR,
      errorMessage: response.statusText,
    }
  }

  async addOrUpdateLanguage(tag: string, appId: string, file: IAppDetailsLanguageFile, isNew: boolean): Promise<IAppLangPublishStatusFetchResult> {
    const response = await this.apiFetch(`/appdefinitions/${appId}/languages/${isNew ? '' : tag}`, {
      method: 'POST',
      body: JSON.stringify({
        languageTag: tag,
        file: file,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (response.status === 200) {
      return {
        type: ApiFetchStatus.SUCCESS,
      };
    }
    return {
      type: ApiFetchStatus.ERROR,
      errorType: AppDetailsFetchErrorType.NETWORK_ERROR,
      errorMessage: response.statusText,
    }
  }

  async updateOwners(payload: IAppOwner[], appId: string, resign?: boolean): Promise<IAppOwnersFetchResult> {
    const url = resign ? `/appdefinitions/${appId}/owner/resign` : `/appdefinitions/v2/${appId}/owner`;
    const result = await this.apiFetch(url, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (result.status != 200) {
      return {
        type: ApiFetchStatus.ERROR,
        errorMessage: result.statusText,
      };
    }
    return { type: ApiFetchStatus.SUCCESS };
  }

  async updateAppIcon(appId: string, icon: Blob, type: 'color' | 'outline'): Promise<IAppIconWriteResult> {
    // Convert the icon to base64 then upload
    const base64Image = await blobToBase64String(icon);

    const result = await this.apiFetch(`/appdefinitions/${appId}/image`, {
      method: 'POST',
      body: JSON.stringify({
        base64String: base64Image,
        type: type,
        name: '', //unused but included to appease the API gods. 
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (result.status != 200) {
      this.logger.error(
        'Network error when writing app icon for app id: %s | reason: %s',
        appId,
        result.statusText
      );
      return {
        type: 'error',
        errorType: 'networkError',
        errorMessage: result.statusText
      };
    }
    const dto = await result.json() as unknown;
    const iconUrl = z.string().parse(dto);
    this.logger.trace('Successfully updated app icon for app id: %s | url: %s', appId, iconUrl);

    const mutationResult = await this.mutateServiceDefinition(appId, (serviceDef) => {
      if (type === 'color') {
        serviceDef.colorIcon = iconUrl;
      } else {
        serviceDef.outlineIcon = iconUrl;
      }
      return serviceDef;
    });
    if (mutationResult.type == 'error') {
      return {
        type: 'error',
        errorType: 'networkError',
        errorMessage: mutationResult.errorMessage ?? '',
      };
    }
    return { type: 'success' }
  }

  async deleteAppIcon(appId: string, type: 'color' | 'outline'): Promise<IAppIconWriteResult> {
    // Convert the icon to base64 then upload
    const result = await this.apiFetch(`/appdefinitions/${appId}/image`, {
      method: 'POST',
      body: JSON.stringify({
        base64String: '',
        type: type,
        name: '', //unused but included to appease the API gods. 
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (result.status != 200) {
      this.logger.error(
        'Network error when writing app icon for app id: %s | reason: %s',
        appId,
        result.statusText
      );
      return {
        type: 'error',
        errorType: 'networkError',
        errorMessage: result.statusText,
      };
    }
    const iconUrl = "";

    // We (strangely) need to write-back this value into the app details
    // Unfortunately, to make matters worse, the API will delete either icon if the other is provided and it is missing. So we have to read the existing
    // app details, get the icon urls, update the new icon url then write it back. 
    // When I invariably plead insanity to the judge, let this be exhibit A.

    const mutationResult = await this.mutateServiceDefinition(appId, (serviceDef) => {
      if (type === 'color') {
        serviceDef.colorIcon = iconUrl;
      } else {
        serviceDef.outlineIcon = iconUrl;
      }
      return serviceDef;
    });
    if (mutationResult.type == 'error') {
      return {
        type: 'error',
        errorType: 'networkError',
        errorMessage: mutationResult.errorMessage ?? '',
      };
    }
    return { type: 'success' }
  }
}
